import React, { useEffect, useState } from "react";
import { superadminAPI } from "../../../api/superadminClient";
import {
  AUDIT_ACTION_COLORS,
  AUDIT_MODULE_LABELS,
  AUDIT_PAGE_SIZE,
  EMPTY_AUDIT_FILTERS,
  formatAuditValue,
  formatDateTime,
} from "./shared";

export default function AuditPanel({ onError }) {
  const [auditFilters, setAuditFilters] = useState(EMPTY_AUDIT_FILTERS);
  const [auditFilterOptions, setAuditFilterOptions] = useState({
    tenants: [],
    modules: [],
    actions: [],
    entityTypes: [],
  });
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditMeta, setAuditMeta] = useState({ total: 0, totalPages: 1 });
  const [auditLoading, setAuditLoading] = useState(false);
  const [expandedAuditId, setExpandedAuditId] = useState(null);

  const loadAuditLogs = async (page = auditPage, filters = auditFilters) => {
    setAuditLoading(true);
    try {
      const params = { page, pageSize: AUDIT_PAGE_SIZE };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const resp = await superadminAPI.getAuditLogs(params);
      setAuditLogs(resp.data?.logs || []);
      setAuditMeta({
        total: resp.data?.total || 0,
        totalPages: resp.data?.totalPages || 1,
      });
    } catch (err) {
      onError(err.response?.data?.error || "Failed to load audit logs");
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    loadAuditLogs(auditPage, auditFilters);
    // Filters reset the page to 1 through handleAuditFilterChange, so paging on
    // either value is enough to keep the table in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditPage, auditFilters]);

  useEffect(() => {
    superadminAPI
      .getAuditLogFilters()
      .then((resp) => setAuditFilterOptions(resp.data || {}))
      .catch(() => {});
  }, []);

  const handleAuditFilterChange = (key, value) => {
    setExpandedAuditId(null);
    setAuditPage(1);
    setAuditFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExportAuditLogs = async () => {
    try {
      const params = {};
      Object.entries(auditFilters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const resp = await superadminAPI.exportAuditLogs(params);
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      onError("Failed to export audit logs");
    }
  };

  return (
    <>
    <section className="superadmin-panel">
      <div className="superadmin-panel-head">
        <h2>Audit Trail</h2>
        <p>
          Every configuration change across all tenants — who did it, when,
          and exactly what changed. Visible here only; tenant admins cannot
          read or alter this history.
        </p>
      </div>

      <div className="superadmin-audit-filters">
        <label>
          Tenant
          <select
            value={auditFilters.tenantId}
            onChange={(e) =>
              handleAuditFilterChange("tenantId", e.target.value)
            }
          >
            <option value="">All tenants</option>
            {(auditFilterOptions.tenants || []).map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Module
          <select
            value={auditFilters.module}
            onChange={(e) =>
              handleAuditFilterChange("module", e.target.value)
            }
          >
            <option value="">All modules</option>
            {(auditFilterOptions.modules || []).map((name) => (
              <option key={name} value={name}>
                {AUDIT_MODULE_LABELS[name] || name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Action
          <select
            value={auditFilters.action}
            onChange={(e) =>
              handleAuditFilterChange("action", e.target.value)
            }
          >
            <option value="">All actions</option>
            {(auditFilterOptions.actions || []).map((name) => (
              <option key={name} value={name}>
                {name.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </label>
        <label>
          Entity
          <select
            value={auditFilters.entityType}
            onChange={(e) =>
              handleAuditFilterChange("entityType", e.target.value)
            }
          >
            <option value="">All entities</option>
            {(auditFilterOptions.entityTypes || []).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Result
          <select
            value={auditFilters.status}
            onChange={(e) =>
              handleAuditFilterChange("status", e.target.value)
            }
          >
            <option value="">All</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILURE">Failure</option>
          </select>
        </label>
        <label>
          From
          <input
            type="date"
            value={auditFilters.from}
            onChange={(e) =>
              handleAuditFilterChange("from", e.target.value)
            }
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={auditFilters.to}
            onChange={(e) => handleAuditFilterChange("to", e.target.value)}
          />
        </label>
        <label>
          Search
          <input
            type="search"
            placeholder="Actor, entity or summary"
            value={auditFilters.search}
            onChange={(e) =>
              handleAuditFilterChange("search", e.target.value)
            }
          />
        </label>
      </div>

      <div className="superadmin-audit-actions">
        <span className="superadmin-subcell">
          {auditLoading
            ? "Loading..."
            : `${auditMeta.total} record(s) — page ${auditPage} of ${auditMeta.totalPages}`}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setAuditFilters(EMPTY_AUDIT_FILTERS)}
            disabled={auditLoading}
          >
            Clear Filters
          </button>
          <button onClick={handleExportAuditLogs} disabled={auditLoading}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="superadmin-table-wrap">
        <table className="superadmin-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Tenant</th>
              <th>Module</th>
              <th>Action</th>
              <th>Entity</th>
              <th>What Changed</th>
              <th>Who</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {auditLogs.length === 0 && !auditLoading ? (
              <tr>
                <td colSpan={8} className="superadmin-subcell">
                  No audit records match these filters.
                </td>
              </tr>
            ) : null}
            {auditLogs.map((log) => (
              <React.Fragment key={log.id}>
                <tr>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {formatDateTime(log.createdAt)}
                  </td>
                  <td>{log.tenantName || "—"}</td>
                  <td>
                    {AUDIT_MODULE_LABELS[log.module] || log.module}
                    <div className="superadmin-subcell">
                      {log.appModule}
                    </div>
                  </td>
                  <td>
                    <span
                      style={{
                        color:
                          AUDIT_ACTION_COLORS[log.action] || "#334155",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      {log.action.replace(/_/g, " ")}
                    </span>
                    {log.status === "FAILURE" ? (
                      <div
                        className="superadmin-subcell"
                        style={{ color: "#dc2626" }}
                      >
                        FAILED
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {log.entityLabel || log.entityId || "—"}
                    <div className="superadmin-subcell">
                      {log.entityType}
                    </div>
                  </td>
                  <td>{log.summary}</td>
                  <td>
                    {log.actorName || "System"}
                    <div className="superadmin-subcell">
                      {log.actorEmail || log.actorType}
                    </div>
                  </td>
                  <td>
                    <button
                      className="superadmin-link-btn"
                      style={{
                        background: "#e0f2fe",
                        color: "#075985",
                        marginTop: 0,
                      }}
                      onClick={() =>
                        setExpandedAuditId(
                          expandedAuditId === log.id ? null : log.id,
                        )
                      }
                    >
                      {expandedAuditId === log.id ? "Hide" : "Details"}
                    </button>
                  </td>
                </tr>

                {expandedAuditId === log.id ? (
                  <tr>
                    <td colSpan={8} className="superadmin-audit-detail">
                      {Array.isArray(log.changes) && log.changes.length ? (
                        <table className="superadmin-table superadmin-audit-diff">
                          <thead>
                            <tr>
                              <th>Field</th>
                              <th>Before</th>
                              <th>After</th>
                            </tr>
                          </thead>
                          <tbody>
                            {log.changes.map((change) => (
                              <tr key={change.field}>
                                <td>
                                  <strong>{change.field}</strong>
                                </td>
                                <td className="superadmin-audit-before">
                                  <pre>
                                    {formatAuditValue(change.before)}
                                  </pre>
                                </td>
                                <td className="superadmin-audit-after">
                                  <pre>
                                    {formatAuditValue(change.after)}
                                  </pre>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="superadmin-subcell">
                          No field-level diff recorded for this event
                          {log.action === "CREATE" || log.action === "DELETE"
                            ? " — see the full snapshot below."
                            : "."}
                        </div>
                      )}

                      <div className="superadmin-audit-meta">
                        <div>
                          <strong>Actor</strong>
                          <div>
                            {log.actorName || "System"} (
                            {log.actorEmail || "no email"}) ·{" "}
                            {log.actorType}
                          </div>
                        </div>
                        <div>
                          <strong>Request</strong>
                          <div>
                            {log.requestMethod || "-"}{" "}
                            {log.requestPath || "-"}
                          </div>
                        </div>
                        <div>
                          <strong>IP Address</strong>
                          <div>{log.ipAddress || "-"}</div>
                        </div>
                        <div>
                          <strong>User Agent</strong>
                          <div>{log.userAgent || "-"}</div>
                        </div>
                        {log.errorMessage ? (
                          <div>
                            <strong>Error</strong>
                            <div style={{ color: "#dc2626" }}>
                              {log.errorMessage}
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {log.before || log.after || log.metadata ? (
                        <div className="superadmin-audit-snapshots">
                          {log.before ? (
                            <div>
                              <strong>Before</strong>
                              <pre>
                                {JSON.stringify(log.before, null, 2)}
                              </pre>
                            </div>
                          ) : null}
                          {log.after ? (
                            <div>
                              <strong>After</strong>
                              <pre>
                                {JSON.stringify(log.after, null, 2)}
                              </pre>
                            </div>
                          ) : null}
                          {log.metadata ? (
                            <div>
                              <strong>Context</strong>
                              <pre>
                                {JSON.stringify(log.metadata, null, 2)}
                              </pre>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="superadmin-audit-actions">
        <button
          onClick={() => setAuditPage((page) => Math.max(1, page - 1))}
          disabled={auditLoading || auditPage <= 1}
        >
          Previous
        </button>
        <button
          onClick={() =>
            setAuditPage((page) =>
              Math.min(auditMeta.totalPages, page + 1),
            )
          }
          disabled={auditLoading || auditPage >= auditMeta.totalPages}
        >
          Next
        </button>
      </div>
    </section>
    </>
  );
}
